<template>
  <div
    ref="panelRef"
    class="ui-panel"
    :class="mainClasses"
  >
    <div
      class="ui-panel__search"
      :class="searchClasses"
    >
      <UIPanelSearch
        v-model="modelValue"
        v-model:storage="modelStorage"
        :disabled
        :placeholder
        :size
        :shrink
        :debounce
        :readonly
      />
    </div>
    <slot
      v-bind="{
        size,
        shrink,
        ButtonCancel,
        ButtonDelete,
        ButtonDuplicate,
        ButtonExport,
        ButtonExportAdvanced,
        ButtonGroup,
        ButtonImport,
        ButtonNew,
        ButtonSaveChanges,
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, useTemplateRef } from 'vue'
import { useResizeObserver } from '@vueuse/core'

import { ButtonSize } from '../Button/utils/types'

import { UIPanelSearch } from '@ui/core'
import ButtonCancel from './components/Cancel.vue'
import ButtonDelete from './components/Delete.vue'
import ButtonDuplicate from './components/Duplicate.vue'
import ButtonExport from './components/Export.vue'
import ButtonExportAdvanced from './components/ExportAdvanced.vue'
import ButtonGroup from './components/Group.vue'
import ButtonImport from './components/Import.vue'
import ButtonNew from './components/ButtonNew.vue'
import ButtonSaveChanges from './components/SaveChanges.vue'


const TINY_VIEW_WIDTH = 480

type Props = {
  placeholder?: string
  size?: ButtonSize
  disabled?: boolean
  unshrinkable?: boolean
  debounce?: boolean
  readonly?: boolean
  disableShrink?: boolean
}

const { unshrinkable, ...props } = defineProps<Props>()

const modelValue = defineModel<string>()
const modelStorage = defineModel<string>('storage')

defineOptions({
  name: 'UIPanel',
})


const panelRef = useTemplateRef('panelRef')

const isTinyView = ref(false)

const shrink = computed(() => isTinyView.value && !unshrinkable && !props.disableShrink)
const size = computed(() => props.size || (shrink.value ? 'xs' : 'sm'))
provide('size', size)

const mainClasses = computed(() => ({
  'ui-panel--shrink': shrink.value,
}))

const searchClasses = computed(() => ({
  'ui-panel__search--shrink': shrink.value,
}))

useResizeObserver(panelRef, () => {
  isTinyView.value = !!panelRef.value?.offsetWidth && panelRef.value?.offsetWidth < TINY_VIEW_WIDTH
})
</script>

<style scoped>
.ui-panel {
  @apply relative;
  @apply flex items-center;
  @apply gap-1 md:gap-2;
  @apply mb-3 pt-1 md:pt-0;

  .ui-dialog__content & {
    @apply mb-0;
  }

  &__search {
    @apply md:min-w-48;
    @apply flex-auto;

    &--shrink {
      @apply w-auto! min-w-0;
      @apply flex items-center justify-start;
      @apply grow-0;
      @apply mr-auto;

      :deep(.ui-button__icon) {
        @apply w-5!;
      }
    }
  }

  &--shrink {
    @apply justify-end;

    :deep(.ui-button-icon) {
      @apply relative;
    }

    :deep(.ui-button):not(.ui-button--primary-filled):not(.ui-button--danger-filled) {
      @apply aspect-square;
    }
  }
}
</style>
